import { useState, useEffect } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Zap, ArrowRight, ShieldCheck, RefreshCw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

import Logo from "../components/Logo";

function formatErr(errOrDetail) {
  if (!errOrDetail) return "Action failed. Please try again.";
  if (errOrDetail?.response?.status === 502 || errOrDetail?.response?.status === 503) {
    return "Server is temporarily unavailable (502). Please try again shortly.";
  }
  const detail = errOrDetail?.response?.data?.detail ?? errOrDetail;
  if (!detail) {
    if (errOrDetail?.message && errOrDetail.message !== "Network Error") return errOrDetail.message;
    return "Action failed. Please try again.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { admin, login, verify2FA, resend2FA } = useAuth();
  const [step, setStep] = useState("login"); // "login" | "2fa"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  if (admin) return <Navigate to="/admin" replace />;

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.require_2fa) {
        setStep("2fa");
        setCooldown(30);
        toast.success(`Verification code sent to ${res.email}`);
      } else {
        toast.success("Welcome back.");
        navigate("/admin");
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) {
      toast.error("Please enter the complete 6-digit verification code.");
      return;
    }
    setLoading(true);
    try {
      await verify2FA(email, otp);
      toast.success("2FA Verification successful! Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      await resend2FA(email);
      setCooldown(30);
      toast.success("Fresh verification code sent to your email!");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3.5 text-sm outline-none focus:border-[#FF5A1F] transition-colors placeholder:text-neutral-400 text-[#0B0B0B]";

  return (
    <div className="min-h-screen grid place-items-center px-5 grid-bg relative overflow-hidden bg-[#F7F6F2]">
      <div className="blob w-[400px] h-[400px] bg-[#FF5A1F] -top-20 -left-20" style={{ opacity: 0.18 }} />

      {step === "login" ? (
        <form onSubmit={submitLogin} data-testid="admin-login-form" className="card-soft rounded-3xl p-8 w-full max-w-md relative z-10">
          <div className="mb-8">
            <Logo />
          </div>

          <h1 className="font-display text-3xl font-bold mb-1 text-[#0B0B0B]">Admin access</h1>
          <p className="text-neutral-500 text-sm mb-7">Sign in to access your administrative workspace.</p>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase text-neutral-400 mb-1">Email Address</label>
              <input
                data-testid="login-email"
                type="email"
                required
                className={inputCls}
                placeholder="admin@neuravolt.in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-neutral-400 mb-1">Password</label>
              <input
                data-testid="login-password"
                type="password"
                required
                className={inputCls}
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button
              data-testid="login-submit"
              disabled={loading}
              className="group w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-colors disabled:opacity-60 mt-2"
            >
              {loading ? "Authenticating..." : "Sign In"}
              {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={submit2FA} data-testid="admin-2fa-form" className="card-soft rounded-3xl p-8 w-full max-w-md relative z-10">
          <div className="flex items-center justify-between mb-6">
            <button
              type="button"
              onClick={() => setStep("login")}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-neutral-500 hover:text-[#FF5A1F] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </button>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF5A1F]/10 text-[#FF5A1F] text-xs font-bold border border-[#FF5A1F]/20">
              <ShieldCheck className="w-3.5 h-3.5" />
              2FA Protected
            </span>
          </div>

          <h1 className="font-display text-2xl font-bold mb-1 text-[#0B0B0B]">Two-Factor Verification</h1>
          <p className="text-neutral-500 text-sm mb-6">
            We sent a 6-digit security code to <strong className="text-[#0B0B0B]">{email}</strong>.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase text-neutral-400 mb-1">Enter 6-Digit Code</label>
              <input
                data-testid="2fa-otp-input"
                type="text"
                maxLength={6}
                required
                autoFocus
                className="w-full bg-[#FAF9F6] border border-black/10 rounded-2xl px-4 py-4 text-center font-mono text-2xl tracking-[0.4em] font-bold outline-none focus:border-[#FF5A1F] transition-colors text-[#0B0B0B]"
                placeholder="123456"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              />
            </div>

            <button
              data-testid="2fa-submit"
              disabled={loading || otp.length < 6}
              className="group w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-colors disabled:opacity-50 shadow-md"
            >
              {loading ? "Verifying Code..." : "Verify Code & Sign In"}
              {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
            </button>

            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={cooldown > 0 || loading}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-500 hover:text-[#FF5A1F] disabled:opacity-50 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                {cooldown > 0 ? `Resend Code in ${cooldown}s` : "Resend Verification Code"}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
